import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  useDisclosure,
  VStack,
  Flex,
  Box,
} from '@chakra-ui/react';
import React, { useRef, useEffect, useMemo, useState } from 'react';
import {
  useForm,
  FormProvider,
  useWatch,
  useFormContext,
} from 'react-hook-form';

import { CreditAgreement } from '@finley-technologies/finley-types';

import { colors } from 'packages/common-frontend/colors';
import { TextInput } from 'packages/common-frontend/components/InputsBETA/TextInput';
import { useGetPartiesByTypeAndCreditFacilityIdsQuery } from 'packages/organization/requests/organization.gql.generated';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { Icon } from '~/packages/common-frontend/components/icons';
import { IsolatedMultiSelect } from '~/packages/common-frontend/components/MultiSelect';
import { Typography } from '~/packages/common-frontend/components/Typography';

export type NumericRangeFilter = {
  min?: number | null;
  max?: number | null;
};

export type LimitedPartnerFiltersFormState = {
  creditFacilityIds?: string[];
  committedCapitalRange: NumericRangeFilter;
  uncalledCapitalRange: NumericRangeFilter;
  netUncalledCapitalRange: NumericRangeFilter;
};

type LimitedPartnerFiltersDrawerProps = {
  activeFilters: LimitedPartnerFiltersFormState;
  onApplyFilters: (newFilters: LimitedPartnerFiltersFormState) => void;
};

export const INITIAL_FILTER_FORM_STATE: LimitedPartnerFiltersFormState = {
  creditFacilityIds: undefined,
  committedCapitalRange: { min: null, max: null },
  uncalledCapitalRange: { min: null, max: null },
  netUncalledCapitalRange: { min: null, max: null },
};

// TODO potentially make a generic component in InputsBETA for this
const MaxRangeInput = ({
  minFieldName,
  maxFieldName,
}: {
  minFieldName: any;
  maxFieldName: any;
}) => {
  const { trigger } = useFormContext();
  const rangeMin = useWatch<LimitedPartnerFiltersFormState>({
    name: minFieldName,
  });

  useEffect(() => {
    trigger([maxFieldName]);
  }, [maxFieldName, rangeMin, trigger]);

  return (
    <TextInput<LimitedPartnerFiltersFormState>
      name={maxFieldName}
      placeholder="$"
      mask="usd"
      customRules={{
        min: {
          value: Number(rangeMin),
          message: 'Maximum must be greater than or equal to minimum',
        },
      }}
    />
  );
};

const getAppliedFiltersCount = (
  filters: LimitedPartnerFiltersFormState,
): number => {
  return Object.values(filters).reduce((count, value) => {
    if (Array.isArray(value) && value.length > 0) {
      return count + 1;
    }
    if (
      typeof value === 'object' &&
      value !== null &&
      Object.values(value).filter((v) => v !== null).length > 0
    ) {
      return count + 1;
    }
    return count;
  }, 0);
};

export const LimitedPartnerFiltersDrawer = ({
  onApplyFilters,
}: LimitedPartnerFiltersDrawerProps) => {
  const drawerControls = useDisclosure();
  const filtersBtnRef = useRef(null);

  const formMethods = useForm<LimitedPartnerFiltersFormState>({
    defaultValues: INITIAL_FILTER_FORM_STATE,
    mode: 'onBlur',
  });

  const [appliedFiltersCount, setAppliedFiltersCount] = useState(0);

  const { facilities } = useCreditFacilitiesOrThrow();
  const allFacilityIds = useMemo(() => facilities.map((f) => f.id), [
    facilities,
  ]);

  const { data: borrowersData } = useGetPartiesByTypeAndCreditFacilityIdsQuery({
    variables: {
      creditFacilityIds: allFacilityIds,
      partyType: CreditAgreement.DomainEntity.Items.PartyType.Borrower,
    },
    skip: allFacilityIds.length === 0,
  });

  const borrowers = useMemo(
    () => borrowersData?.partiesByTypeAndCreditFacilityIds ?? [],
    [borrowersData],
  );

  const [selectedBorrowerIds, setSelectedBorrowerIds] = useState<string[]>([]);
  const [borrowersInitialized, setBorrowersInitialized] = useState(false);

  useEffect(() => {
    if (borrowers.length > 0 && !borrowersInitialized) {
      setSelectedBorrowerIds(borrowers.map((b) => b.id));
      setBorrowersInitialized(true);
    }
  }, [borrowers, borrowersInitialized]);

  const filteredFacilities = useMemo(
    () => facilities.filter((f) => selectedBorrowerIds.includes(f.borrowerId)),
    [facilities, selectedBorrowerIds],
  );

  const handleBorrowerChange = (newBorrowerIds: string[]): void => {
    setSelectedBorrowerIds(newBorrowerIds);
    const validFacilityIds = new Set(
      facilities
        .filter((f) => newBorrowerIds.includes(f.borrowerId))
        .map((f) => f.id),
    );
    const currentFacilityIds = formMethods.getValues('creditFacilityIds');
    const updatedFacilityIds =
      currentFacilityIds === undefined
        ? undefined
        : currentFacilityIds.filter((id) => validFacilityIds.has(id));
    formMethods.setValue('creditFacilityIds', updatedFacilityIds);
  };

  const handleResetFilters = (): void => {
    formMethods.reset(INITIAL_FILTER_FORM_STATE);
    setSelectedBorrowerIds(borrowers.map((b) => b.id));
    onApplyFilters(INITIAL_FILTER_FORM_STATE);
    setAppliedFiltersCount(0);
  };

  const onSubmit = (formData: LimitedPartnerFiltersFormState) => {
    onApplyFilters(formData);
    setAppliedFiltersCount(getAppliedFiltersCount(formData));
    drawerControls.onClose();
  };

  return (
    <>
      <Button
        variant="secondary"
        colorScheme="v2-aurora"
        ref={filtersBtnRef}
        onClick={drawerControls.onOpen}
        leftIcon={<Icon icon="filter" />}
        rightIcon={
          appliedFiltersCount > 0 ? (
            <Box
              background={colors.gray[100]}
              borderRadius="0.25rem"
              padding="0.125rem 0.5rem"
            >
              {appliedFiltersCount}
            </Box>
          ) : undefined
        }
        p="0.5rem 1.5rem"
      >
        Filters
      </Button>
      <Drawer
        placement="right"
        finalFocusRef={filtersBtnRef}
        size="lg"
        isOpen={drawerControls.isOpen}
        onClose={drawerControls.onClose}
      >
        <FormProvider {...formMethods}>
          <DrawerOverlay />
          <form onSubmit={formMethods.handleSubmit(onSubmit)}>
            <DrawerContent>
              <DrawerCloseButton />
              <DrawerHeader
                textAlign="left"
                mt="1.5rem"
                px="3rem"
                pb="0"
                mb="0.5rem"
              >
                <Typography variant="page-title">Filters</Typography>
              </DrawerHeader>
              <DrawerBody px="3rem">
                <VStack spacing="2.5rem" align="flex-start">
                  <FormControl>
                    <FormLabel mb="1rem">
                      <Flex direction="column" gap="1rem">
                        <Typography variant="section-title" trim>
                          Borrower
                        </Typography>
                        <Typography variant="detail-muted" trim>
                          Filter by borrower. Selecting a borrower narrows the
                          credit facility options.
                        </Typography>
                      </Flex>
                    </FormLabel>
                    <IsolatedMultiSelect
                      autoApplyOptions
                      initialSelections={selectedBorrowerIds}
                      isSearchable
                      onApplyOptions={handleBorrowerChange}
                      options={borrowers.map((b) => ({
                        label: b.name,
                        value: b.id,
                      }))}
                      optionsHeader="Borrowers"
                      placeholderText="Select borrowers"
                      showSelectedCount
                      titleText="All borrowers"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel mb="1rem">
                      <Flex direction="column" gap="1rem">
                        <Typography variant="section-title" trim>
                          Credit Facility
                        </Typography>
                        <Typography variant="detail-muted" trim>
                          Filter by one or more credit facilities.
                        </Typography>
                      </Flex>
                    </FormLabel>
                    <IsolatedMultiSelect
                      autoApplyOptions
                      initialSelections={
                        formMethods.watch('creditFacilityIds') ??
                        filteredFacilities.map((f) => f.id)
                      }
                      isSearchable
                      onApplyOptions={(selectedIds) => {
                        formMethods.setValue('creditFacilityIds', selectedIds);
                      }}
                      options={filteredFacilities.map((f) => ({
                        label: f.name,
                        value: f.id,
                      }))}
                      optionsHeader="Credit facilities"
                      placeholderText="Select credit facilities"
                      showSelectedCount
                      titleText="All credit facilities"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel mb="1rem">
                      <Flex direction="column" gap="1rem">
                        <Typography variant="section-title" trim>
                          Committed Capital
                        </Typography>
                        <Typography variant="detail-muted" trim>
                          Filter by minimum or maximum Committed Capital amount.
                        </Typography>
                      </Flex>
                    </FormLabel>
                    <Flex gap="1rem">
                      <Flex direction="column" gap="0.5rem" width="100%">
                        <Typography variant="detail" trim fontWeight="medium">
                          Minimum Amount
                        </Typography>
                        <TextInput<LimitedPartnerFiltersFormState>
                          name="committedCapitalRange.min"
                          placeholder="$"
                          mask="usd"
                        />
                        <Typography variant="detail-muted" trim>
                          Optional
                        </Typography>
                      </Flex>
                      <Flex direction="column" gap="0.5rem" width="100%">
                        <Typography variant="detail" trim fontWeight="medium">
                          Maximum Amount
                        </Typography>
                        <MaxRangeInput
                          minFieldName="committedCapitalRange.min"
                          maxFieldName="committedCapitalRange.max"
                        />
                        <Typography variant="detail-muted" trim>
                          Optional
                        </Typography>
                      </Flex>
                    </Flex>
                  </FormControl>

                  <FormControl>
                    <FormLabel mb="1rem">
                      <Flex direction="column" gap="1rem">
                        <Typography variant="section-title" trim>
                          Uncalled Capital
                        </Typography>
                        <Typography variant="detail-muted" trim>
                          Filter by minimum or maximum Uncalled Capital amount.
                        </Typography>
                      </Flex>
                    </FormLabel>
                    <Flex gap="1rem">
                      <Flex direction="column" gap="0.5rem" width="100%">
                        <Typography variant="detail" trim fontWeight="medium">
                          Minimum Amount
                        </Typography>
                        <TextInput<LimitedPartnerFiltersFormState>
                          name="uncalledCapitalRange.min"
                          placeholder="$"
                          mask="usd"
                        />
                        <Typography variant="detail-muted" trim>
                          Optional
                        </Typography>
                      </Flex>
                      <Flex direction="column" gap="0.5rem" width="100%">
                        <Typography variant="detail" trim fontWeight="medium">
                          Maximum Amount
                        </Typography>
                        <MaxRangeInput
                          minFieldName="uncalledCapitalRange.min"
                          maxFieldName="uncalledCapitalRange.max"
                        />
                        <Typography variant="detail-muted" trim>
                          Optional
                        </Typography>
                      </Flex>
                    </Flex>
                  </FormControl>

                  <FormControl>
                    <FormLabel mb="1rem">
                      <Flex direction="column" gap="1rem">
                        <Typography variant="section-title" trim>
                          Net Uncalled Capital
                        </Typography>
                        <Typography variant="detail-muted" trim>
                          Filter by minimum or maximum Net Uncalled Capital
                          amount.
                        </Typography>
                      </Flex>
                    </FormLabel>
                    <Flex gap="1rem">
                      <Flex direction="column" gap="0.5rem" width="100%">
                        <Typography variant="detail" trim fontWeight="medium">
                          Minimum Amount
                        </Typography>
                        <TextInput<LimitedPartnerFiltersFormState>
                          name="netUncalledCapitalRange.min"
                          placeholder="$"
                          mask="usd"
                        />
                        <Typography variant="detail-muted" trim>
                          Optional
                        </Typography>
                      </Flex>
                      <Flex direction="column" gap="0.5rem" width="100%">
                        <Typography variant="detail" trim fontWeight="medium">
                          Maximum Amount
                        </Typography>
                        <MaxRangeInput
                          minFieldName="netUncalledCapitalRange.min"
                          maxFieldName="netUncalledCapitalRange.max"
                        />
                        <Typography variant="detail-muted" trim>
                          Optional
                        </Typography>
                      </Flex>
                    </Flex>
                  </FormControl>
                </VStack>
              </DrawerBody>

              <DrawerFooter justifyContent="space-between" m="1.5rem">
                <Button
                  variant="secondary"
                  colorScheme="v2-aurora"
                  onClick={handleResetFilters}
                >
                  Clear All
                </Button>
                <Button variant="primary" colorScheme="v2-aurora" type="submit">
                  Apply Filters
                </Button>
              </DrawerFooter>
            </DrawerContent>
          </form>
        </FormProvider>
      </Drawer>
    </>
  );
};
